import React, { useEffect, useRef, useState } from "react";
import Script from "next/script";
import { useIntersectionObserver } from "usehooks-ts";

type Props = {
  playerId: string;
  playerScript: string;
  playlist?: string;
  file?: string;
};

const Video = ({ playerId, playerScript, playlist, file }: Props) => {
  if (!playlist && !file) {
    throw new Error("Video component must have playlist or file defined");
  }
  // We need to ensure the script is loaded, if the video is in view
  // there can be a race condition where the script is not loaded yet
  // and the player is initialized before the script is loaded.
  const [isPlayerScriptLoaded, setIsPlayerScriptLoaded] = useState(false);
  // to ensure we only load the player once when in view
  const { ref, isIntersecting } = useIntersectionObserver({
    freezeOnceVisible: true,
  });

  // using a ref instead of the id to initialize the player
  // to avoid race conditions when trying to setup the player.
  const playerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      isIntersecting &&
      isPlayerScriptLoaded &&
      playerRef.current &&
      window.jwplayer
    ) {
      // For more information about the JWPlayer docs:
      // https://docs.jwplayer.com/players/reference/configuration-options-getting-started
      window.jwplayer(playerRef.current).setup({
        file,
        playlist,
      });
    }
  }, [isPlayerScriptLoaded, isIntersecting, file, playlist, playerRef.current]);

  return (
    <>
      {/* For more information about the cloud-hosted player:
        https://docs.jwplayer.com/players/docs/add-a-cloud-hosted-player */}
      <Script
        src={playerScript}
        id={`jwplayer-script-${playerId}`}
        onLoad={() => setIsPlayerScriptLoaded(true)}
      />
      <div ref={ref} className="bg-leafly-white image--16:9 relative">
        <div className="absolute w-full h-full">
          <div id={playerId} ref={playerRef} />
        </div>
      </div>
    </>
  );
};

export default Video;
